import React, { useEffect, useRef, useState } from 'react';

interface StoryboardCue {
  start: number;
  end: number;
  x: number;
  y: number;
  w: number;
  h: number;
}

interface StoryboardPreviewProps {
  videoRef: React.RefObject<HTMLVideoElement>;
  storyboardUrl?: string;
  enabled?: boolean;
}

function parseStoryboardVtt(
  vttText: string,
  vttUrl: string
): { cues: StoryboardCue[]; spriteUrl: string | null } {
  const cues: StoryboardCue[] = [];
  let spriteUrl: string | null = null;
  const baseUrl = vttUrl.substring(0, vttUrl.lastIndexOf('/') + 1);
  const lines = vttText.split(/\r?\n/);

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    const tm = line.match(
      /^(\d+):(\d+):(\d+)\.(\d+)\s+-->\s+(\d+):(\d+):(\d+)\.(\d+)/
    );
    if (!tm) continue;

    const start =
      parseInt(tm[1]) * 3600 +
      parseInt(tm[2]) * 60 +
      parseInt(tm[3]) +
      parseInt(tm[4]) / 1000;
    const end =
      parseInt(tm[5]) * 3600 +
      parseInt(tm[6]) * 60 +
      parseInt(tm[7]) +
      parseInt(tm[8]) / 1000;

    const payload = (lines[i + 1] || '').trim();
    const pm = payload.match(/^(.+?)#xywh=(\d+),(\d+),(\d+),(\d+)/);
    if (!pm) continue;

    if (!spriteUrl) {
      const ref = pm[1];
      spriteUrl = /^https?:\/\//.test(ref) ? ref : baseUrl + ref;
    }

    cues.push({
      start,
      end,
      x: parseInt(pm[2]),
      y: parseInt(pm[3]),
      w: parseInt(pm[4]),
      h: parseInt(pm[5]),
    });
  }

  return { cues, spriteUrl };
}

const isMobileUA = () =>
  typeof navigator !== 'undefined' &&
  /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

// Mark new progress-bar variants with `data-storyboard-target` to opt them
// into hover preview. Native HTML5 controls handle hover internally — skip.
const PROGRESS_SELECTOR = '[data-storyboard-target]';

export const StoryboardPreview: React.FC<StoryboardPreviewProps> = ({
  videoRef,
  storyboardUrl,
  enabled,
}) => {
  const previewRef = useRef<HTMLDivElement>(null);
  const [data, setData] = useState<{
    cues: StoryboardCue[];
    spriteUrl: string;
    frameW: number;
    frameH: number;
  } | null>(null);

  // Fetch + parse the VTT once per URL.
  useEffect(() => {
    if (!enabled || !storyboardUrl || isMobileUA()) {
      setData(null);
      return;
    }

    let cancelled = false;
    fetch(storyboardUrl)
      .then((res) => {
        if (!res.ok) throw new Error('VTT fetch ' + res.status);
        return res.text();
      })
      .then((text) => {
        if (cancelled) return;
        const { cues, spriteUrl } = parseStoryboardVtt(text, storyboardUrl);
        if (!cues.length || !spriteUrl) return;
        // Pré-carrega o sprite (1 request, fica cacheado).
        const img = new Image();
        img.src = spriteUrl;
        setData({
          cues,
          spriteUrl,
          frameW: cues[0].w,
          frameH: cues[0].h,
        });
      })
      .catch((err) => {
        console.warn('Storyboard load failed:', err.message);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, storyboardUrl]);

  // Attach mousemove/leave to every progress-bar variant present in the same
  // container. MutationObserver picks up bars created later (theme switches,
  // dynamic toggles).
  useEffect(() => {
    if (!data) return;
    const previewEl = previewRef.current;
    if (!previewEl) return;

    const container = previewEl.parentElement;
    if (!container) return;

    const findCue = (timeSec: number): StoryboardCue | null => {
      for (let i = 0; i < data.cues.length; i++) {
        if (timeSec >= data.cues[i].start && timeSec < data.cues[i].end) {
          return data.cues[i];
        }
      }
      return data.cues[data.cues.length - 1] || null;
    };

    const showPreviewAt = (bar: HTMLElement, clientX: number) => {
      const video = videoRef.current;
      if (!video || !video.duration || !isFinite(video.duration)) return;

      const barRect = bar.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      const x = Math.max(0, Math.min(barRect.width, clientX - barRect.left));
      const percent = barRect.width > 0 ? x / barRect.width : 0;
      const timeSec = percent * video.duration;

      const cue = findCue(timeSec);
      if (!cue) return;

      const previewLeft =
        barRect.left - containerRect.left + x - data.frameW / 2;
      const clamped = Math.max(
        4,
        Math.min(containerRect.width - data.frameW - 4, previewLeft)
      );
      // Anchor to the bottom of the bar (preview floats above it).
      const previewBottom = containerRect.bottom - barRect.top + 8;

      previewEl.style.left = clamped + 'px';
      previewEl.style.bottom = previewBottom + 'px';
      previewEl.style.backgroundPosition = `-${cue.x}px -${cue.y}px`;
      previewEl.style.opacity = '1';
      previewEl.style.display = 'block';
    };

    const hidePreview = () => {
      previewEl.style.opacity = '0';
      previewEl.style.display = 'none';
    };

    const attached = new WeakSet<HTMLElement>();
    const handlersByEl = new WeakMap<
      HTMLElement,
      { move: (e: MouseEvent) => void; leave: () => void }
    >();

    const attachedList: HTMLElement[] = [];

    const attachAll = () => {
      const bars = container.querySelectorAll<HTMLElement>(PROGRESS_SELECTOR);
      bars.forEach((bar) => {
        if (attached.has(bar)) return;
        attached.add(bar);
        attachedList.push(bar);
        const move = (e: MouseEvent) => showPreviewAt(bar, e.clientX);
        const leave = hidePreview;
        bar.addEventListener('mousemove', move);
        bar.addEventListener('mouseleave', leave);
        handlersByEl.set(bar, { move, leave });
      });
    };

    attachAll();
    const observer = new MutationObserver(attachAll);
    observer.observe(container, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      attachedList.forEach((bar) => {
        const handlers = handlersByEl.get(bar);
        if (handlers) {
          bar.removeEventListener('mousemove', handlers.move);
          bar.removeEventListener('mouseleave', handlers.leave);
        }
      });
    };
  }, [data, videoRef]);

  if (!enabled || !storyboardUrl || !data) return null;

  return (
    <div
      ref={previewRef}
      style={{
        position: 'absolute',
        width: data.frameW,
        height: data.frameH,
        pointerEvents: 'none',
        borderRadius: 6,
        border: '2px solid rgba(255,255,255,0.9)',
        boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
        backgroundImage: `url("${data.spriteUrl}")`,
        backgroundRepeat: 'no-repeat',
        display: 'none',
        zIndex: 999,
        transition: 'opacity 80ms linear',
        opacity: 0,
      }}
    />
  );
};
